import { ChartOptions, ScriptableContext, TooltipItem } from 'chart.js'
import dayjs from 'dayjs'
import { createChartDataArray, formatChartDateLabel } from 'utils/chartjs.ts'
import { GetMarketPricesChartDatasets, MarketPricesBarDataset, MarketPricesChartOptions } from 'components/charts/market-prices/types.ts'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants.ts'
import { ExternalTooltipContext } from 'components/charts/types.ts'
import { HOUR_LABEL_FORMAT } from 'constants/datetime.ts'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { formatAmount } from 'utils/format.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'
import { getMarketPriceBarColor } from 'components/charts/market-prices/utils.ts'

/**
 * Get market prices chart options
 * @param {MarketPricesChartOptions} params
 */
export const getMarketPricesChartOptions = ({
  granularity,
  labels,
  handleHoverDataPoint,
  isMobile,
  isCompact
}: MarketPricesChartOptions): ChartOptions<'bar'> => {
  const defaults = getBarChartDefaults({ isMobile })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    // Overrides the default barPercentage of the bar charts, which is narrower on mobile
    datasets: { bar: { barPercentage: 1 } },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    layout: {
      padding: {
        ...defaults.layout.padding,
        left: 5,
        right: 5
      }
    },
    scales: {
      y: {
        ...y,
        ticks: {
          ...y.ticks,
          callback: (value: string | number) => formatAmount(Number(value), { fractionDigits: 2 })
        }
      },
      x: {
        grid: {
          lineWidth: 0
        },
        ticks: {
          ...x.ticks,
          // The category scale passes the index of the label as value
          callback: (value: string | number) => formatChartDateLabel(labels[Number(value)], granularity)
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: isCompact ? '' : '€ / kWh'
        }
      },
      tooltip: {
        ...TOOLTIP_CONFIG,
        external: (context: ExternalTooltipContext<'bar'>) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({ context, totalColumns: labels.length })
        },
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) => dayjs(context?.[0]?.label).format(HOUR_LABEL_FORMAT)
        }
      }
    }
  }
}

export const getMarketPricesChartDatasets = ({
  labels,
  data,
  lowestIndexes,
  currentIndex,
  compact,
  isProducer
}: GetMarketPricesChartDatasets): MarketPricesBarDataset[] => {
  const borderRadius = compact ? 2 : 4
  const maxBarThickness = 28
  const getBarColor = ({ dataIndex }: ScriptableContext<'bar'>, isHovered?: boolean) =>
    CHART_COLORS[getMarketPriceBarColor({ index: dataIndex, lowestIndexes, currentIndex, isProducer, isHovered })]

  return [
    {
      type: 'bar' as const,
      backgroundColor: (context: ScriptableContext<'bar'>) => getBarColor(context),
      hoverBackgroundColor: (context: ScriptableContext<'bar'>) => getBarColor(context, true),
      data: createChartDataArray(
        labels?.length,
        data.map(({ price }) => price || 0)
      ),
      borderSkipped: 'start',
      maxBarThickness,
      borderRadius
    }
  ]
}
